// cncExpense.ts — CNC 기계경비(₩/hr) 순수 계산: 마스터 경비 행(파라미터) → 시간당 경비.
//
// 백엔드 `apps/api/api/core/cost/cnc_expense.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// 기존 overhead.ts(공정명 → master 상수 lookup)와 달리 본 모듈은 파라미터 주도형이다:
// CNC 마스터 경비 테이블의 한 행(DB row)을 그대로 받아 숫자만 반환한다.
//
// 수식 (CNC 견적 세부원가 — 경비 buildup):
//   기계상각비 = 장비가액 / 설비년수 / 년작업일수 / 일작업시간
//   건물상각비 = 설치면적 × 부대설비비율 × 건축비용 / 건물년수 / 년작업일수 / 일작업시간
//   수선비     = (기계상각비 + 건물상각비) × 수선비율
//   전력비     = 전력량 × 부하율 × 전력단가
//   직접경비   = 기계상각비 + 건물상각비 + 수선비 + 전력비
//   간접경비   = 직접경비 × 간접경비율
//   기계경비최종(₩/hr) = 직접경비 + 간접경비
//
// 최종검사/포장 처럼 설비가 없는 공정은 모든 수치가 0 인 행으로 표현된다 —
// 0 나눗셈은 safeDiv 가드로 0 을 반환한다 (NaN/Infinity 금지).

// ----- 입력 / 출력 -----

export interface MachineExpenseParams {
  equipmentPrice: number; // 장비가액 (₩)
  areaM2: number; // 설치면적 (㎡)
  buildingYears: number; // 건물년수
  equipmentYears: number; // 설비년수
  yearlyWorkDays: number; // 년작업일수 (일)
  dailyWorkHours: number; // 일작업시간 (h)
  auxEquipmentRatio: number; // 부대설비비율
  buildingCost: number; // 건축비용 (₩/㎡)
  repairRatio: number; // 수선비율
  powerKw: number; // 전력량 (kW)
  loadRatio: number; // 부하율
  powerUnitPrice: number; // 전력단가 (₩/kWh)
  indirectExpenseRatio: number; // 간접경비율
}

export interface MachineExpenseResult {
  machineDep: number; // 기계상각비 = 장비가액 / 설비년수 / 년작업일수 / 일작업시간
  buildingDep: number; // 건물상각비 = 설치면적 × 부대설비비율 × 건축비용 / 건물년수 / 년작업일수 / 일작업시간
  repair: number; // 수선비 = (기계상각 + 건물상각) × 수선비율
  power: number; // 전력비 = 전력량 × 부하율 × 전력단가
  direct: number; // 직접경비 = 4개 합
  indirect: number; // 간접경비 = 직접경비 × 간접경비율
  hourlyRate: number; // 기계경비최종(₩/hr) = 직접 + 간접
}

// 0 나눗셈 가드 — all-zero 행(최종검사/포장)이 NaN/Infinity 없이 모두 0 이 되도록.
const safeDiv = (n: number, d: number): number => (d === 0 ? 0 : n / d);

// 마스터 경비 행 1개의 기계경비(₩/hr)를 산출한다.
export function machineExpense(p: MachineExpenseParams): MachineExpenseResult {
  const machineDep = safeDiv(
    p.equipmentPrice,
    p.equipmentYears * p.yearlyWorkDays * p.dailyWorkHours
  );
  const buildingDep = safeDiv(
    p.areaM2 * p.auxEquipmentRatio * p.buildingCost,
    p.buildingYears * p.yearlyWorkDays * p.dailyWorkHours
  );
  const repair = (machineDep + buildingDep) * p.repairRatio;
  const power = p.powerKw * p.loadRatio * p.powerUnitPrice;
  const direct = machineDep + buildingDep + repair + power;
  const indirect = direct * p.indirectExpenseRatio;
  const hourlyRate = direct + indirect;
  return { machineDep, buildingDep, repair, power, direct, indirect, hourlyRate };
}
